import React from 'react';
import { Heart, Plus, Share2, Scale, Settings2, Zap } from 'lucide-react';
import { getProductImageUrl } from '../utils/images';

const ProductCard = ({ product, favorites, toggleFavorite, addToCart, openProduct, handleShare, toggleCompare, isComparing, displayMode = 'grid', formatPrice, isAdmin, isFlashSaleActive, isFlashSaleComingSoon }) => {
  const isFav = Array.isArray(favorites) && favorites.find(f => f.id === product.id);
  // Considère en rupture UNIQUEMENT si c'est en fin de série
  // Les produits sur commande peuvent être ajoutés au panier même si stock = 0
  const isOutOfStock = product?.isEndOfSeries && (product?.stock || 0) <= 0;
  const hasOptions = product.options && product.options.length > 0;

  // Utilise la fonction passée ou une logique par défaut si non fournie
  const flashActive = typeof isFlashSaleActive === 'function' ? isFlashSaleActive(product) : product.isFlashSale;
  
  const flashScheduled = typeof isFlashSaleComingSoon === 'function' ? isFlashSaleComingSoon(product) : false;

  if (displayMode === 'list') {
    return (
      <div className="group relative bg-white dark:bg-slate-900 rounded-[24px] border border-slate-100 dark:border-slate-800 p-4 hover:shadow-xl transition-all duration-500 flex flex-col sm:flex-row gap-6 items-center">
        <div onClick={() => openProduct(product)} className="w-full sm:w-48 h-48 sm:h-32 flex-shrink-0 overflow-hidden bg-slate-50 dark:bg-slate-800 rounded-[16px] relative cursor-pointer">
          <img src={getProductImageUrl(product)} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt={`${product.brand} ${product.label}`.trim() || 'Produit'} />
          {flashActive && (
            <div className="absolute top-2 left-2 bg-amber-500 text-white p-1.5 rounded-lg shadow-lg animate-pulse">
              <Zap className="w-3 h-3 fill-current" />
            </div>
          )}
          {flashScheduled && (
            <div className="absolute top-2 left-2 bg-blue-500 text-white px-2 py-1 rounded-lg shadow-lg text-[8px] font-black uppercase tracking-widest">
              Bientôt
            </div>
          )}
        </div>
        
        <div className="flex-1 text-left w-full">
          <div className="flex justify-between items-start gap-4 mb-2">
            <div onClick={() => openProduct(product)} className="cursor-pointer">
              <h3 className="font-black dark:text-white text-lg tracking-tight mb-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">{product.label}</h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{product.brand}</p>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={(e) => toggleFavorite(e, product)}
                className={`p-2.5 rounded-xl transition-all ${isFav ? 'bg-red-500 text-white shadow-lg' : 'bg-slate-50 dark:bg-slate-800 text-slate-400 hover:text-red-500'}`}
              >
                <Heart className={`w-4 h-4 ${isFav ? 'fill-current' : ''}`} />
              </button>
              <button 
                onClick={(e) => toggleCompare(e, product)}
                className={`p-2.5 rounded-xl transition-all ${isComparing ? 'bg-blue-600 text-white shadow-lg' : 'bg-slate-50 dark:bg-slate-800 text-slate-400 hover:text-blue-500'}`}
              >
                <Scale className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-4">
              {flashActive && product.flashSaleDiscount > 0 ? (
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-black dark:text-white tracking-tighter">
                      {formatPrice(product.price * (1 - product.flashSaleDiscount / 100))}
                    </span>
                    <span className="bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-lg">
                      -{product.flashSaleDiscount}%
                    </span>
                  </div>
                  <span className="text-sm text-slate-400 line-through font-bold">
                    {formatPrice(product.price)}
                  </span>
                </div>
              ) : (
                <span className="text-2xl font-black dark:text-white tracking-tighter">{formatPrice(product.price || 0)}</span>
              )}
              {product.isEndOfSeries && (
                <span className="bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded-full font-black text-[8px] uppercase tracking-widest border border-amber-200 dark:border-amber-800">
                  Fin de série
                </span>
              )}
            </div>
            
            {hasOptions ? (
              <button 
                type="button"
                onClick={() => openProduct(product)}
                className="px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center gap-2 bg-slate-900 dark:bg-blue-600 text-white hover:bg-blue-700 dark:hover:bg-blue-500"
              >
                <Settings2 className="w-4 h-4" /> Options
              </button>
            ) : (
              <button
                type="button"
                onClick={() => addToCart(null, product)}
                disabled={isOutOfStock}
                className={`px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center gap-2 ${isOutOfStock ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed' : 'bg-slate-900 dark:bg-blue-600 text-white hover:bg-blue-700 dark:hover:bg-blue-500'}`}
              >
                <Plus className="w-4 h-4" /> Panier
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-2 hover:shadow-xl transition-all duration-500">
      <div className="absolute top-2 left-2 z-10 flex flex-col gap-1">
        {flashActive && (
          <div className="bg-amber-500 text-white p-2 rounded-xl shadow-lg animate-pulse flex items-center justify-center">
            <Zap className="w-4 h-4 fill-current" />
          </div>
        )}
        {flashScheduled && (
          <div className="bg-blue-500 text-white px-3 py-1.5 rounded-xl shadow-lg flex items-center justify-center text-[8px] font-black uppercase tracking-widest">
            Bientôt
          </div>
        )}
        {product.isEndOfSeries && (
          <span className="bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 px-3 py-1 rounded-full font-black text-[8px] uppercase tracking-widest border border-amber-200 dark:border-amber-800 shadow-sm">
            Fin de série
          </span>
        )}
      </div>
      {/* N'afficher "Sur commande" seulement si ce n'est PAS une fin de série */}
      {(!product?.isEndOfSeries) && ((product?.stock || 0) <= 0 || product?.onOrder) && (
        <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-10 pointer-events-none">
          <span className="bg-amber-500 text-white px-4 py-1 rounded-full font-black text-xs uppercase tracking-widest shadow-lg whitespace-nowrap">Sur commande</span>
        </div>
      )}
      <div className="absolute top-2 right-2 z-10 flex flex-col gap-1">
        <button 
          onClick={(e) => toggleFavorite(e, product)}
          className={`p-1.5 md:p-2 rounded-lg transition-all ${isFav ? 'bg-red-500 text-white shadow-lg' : 'bg-white/80 dark:bg-slate-800/80 text-slate-400 hover:text-red-500'}`}
          aria-label={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
        >
          <Heart className={`w-3 h-3 md:w-4 md:h-4 ${isFav ? 'fill-current' : ''}`} />
        </button>
        <button 
          onClick={(e) => toggleCompare(e, product)}
          className={`p-1.5 md:p-2 rounded-lg transition-all ${isComparing ? 'bg-blue-600 text-white shadow-lg' : 'bg-white/80 dark:bg-slate-800/80 text-slate-400 hover:text-blue-500'}`}
          aria-label="Comparer"
        >
          <Scale className="w-3 h-3 md:w-4 md:h-4" />
        </button>
        <button 
          onClick={(e) => handleShare(e, product)}
          className="p-1.5 md:p-2 rounded-lg bg-white/80 dark:bg-slate-800/80 text-slate-400 hover:text-blue-500 transition-all"
          aria-label="Partager"
        >
          <Share2 className="w-5 h-5" />
        </button>
      </div>
      <div onClick={() => openProduct(product)} className="aspect-square overflow-hidden bg-slate-50 dark:bg-slate-800 rounded-[24px] cursor-pointer">
        <img src={getProductImageUrl(product)} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt={`${product.brand} ${product.label}`.trim() || 'Produit'} />
      </div>
      <div className="p-2 md:p-3 text-left">
        <h3 onClick={() => openProduct(product)} className="text-xs md:text-sm font-black dark:text-white line-clamp-2 mb-1 min-h-[2rem] tracking-tight cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors">{product.label}</h3>
        <p className="text-[8px] md:text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-2">{product.brand}</p>
        <div className="flex items-center justify-between border-t dark:border-slate-800 pt-2 relative z-20">
          {flashActive && product.flashSaleDiscount > 0 ? (
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="text-sm md:text-lg font-black dark:text-white tracking-tighter">
                  {formatPrice(product.price * (1 - product.flashSaleDiscount / 100))}
                </span>
                <span className="bg-red-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-lg">
                  -{product.flashSaleDiscount}%
                </span>
              </div>
              <span className="text-xs text-slate-400 line-through font-bold">
                {formatPrice(product.price)}
              </span>
            </div>
          ) : (
            <span className="text-sm md:text-lg font-black dark:text-white tracking-tighter">{formatPrice(product.price || 0)}</span>
          )}
          {hasOptions ? (
            <button 
              type="button"
              onClick={() => openProduct(product)}
              className="px-2 md:px-3 py-1.5 md:py-2 rounded-lg md:rounded-xl bg-slate-900 dark:bg-blue-600 text-white hover:bg-blue-700 dark:hover:bg-blue-500 transition-all flex items-center gap-1 md:gap-2"
            >
              <Settings2 className="w-3 h-3 md:w-4 md:h-4" />
              <span className="text-[10px] md:text-xs font-black uppercase">Options</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => addToCart(null, product)}
              disabled={isOutOfStock}
              className={`p-1.5 md:p-2 rounded-lg md:rounded-xl transition-all ${isOutOfStock ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed' : 'bg-slate-900 dark:bg-blue-600 text-white hover:bg-blue-700 dark:hover:bg-blue-500'}`}
            >
              <Plus className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
